import React, { useEffect, useMemo } from 'react'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Button, Icon, Input } from '../../../../../../components/UI'
import ErrorMessageWithExclamation from '../../../../../../components/UI/ErrorMessageWithExclamation'
import MyQuizTemplateCreatePreviewQuestion from './MyQuizTemplateCreatePreviewQuestion'

import {
  selectErrorAddQuiz,
  selectErrorEditQuiz,
  selectLoadingAddQuiz,
  selectLoadingEditQuiz,
} from '../../../../../../routing/fetchingfunction/quiz/quiz.reducer'
import { addQuiz, editQuiz } from '../../../../../../routing/fetchingfunction/quiz/quiz.actions'
import { selectRole } from '../../../../../../routing/fetchingfunction/user/user.reducer'

import { useAvailableCategories } from '../../../../../../hooks'

import { scrollToTop, handleNumberInputWheel } from '../../../../../../helpers'

const MyQuizTemplateCreatePreview = ({ quiz, setQuizTemplateData, setStep, quizTemplateData }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const role = useSelector(selectRole)
  const isLoading = useSelector(selectLoadingAddQuiz)
  const editIsLoading = useSelector(selectLoadingEditQuiz)
  const error = useSelector(selectErrorAddQuiz)
  const editError = useSelector(selectErrorEditQuiz)

  const {
    register,
    getValues,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      minim_correct_answers: quizTemplateData?.minim_correct_answers || '',
    },
    resolver: yupResolver(
      yup.object({
        minim_correct_answers: yup
          .number()
          .typeError('Minimum Correct Answers to Complete is required')
          .required('Minimum Correct Answers to Complete is required')
          .min(1, 'At least 1 answer')
          .max(
            quizTemplateData?.questions?.length,
            'Minimum Answers cannot be more than number questions'
          ),
      })
    ),
  })

  const { passedCategories } = useAvailableCategories('passed')

  const category = useMemo(() => {
    return passedCategories.find((category) => category.id === +quizTemplateData.category)
  }, [passedCategories, quizTemplateData])

  const handleFormSubmit = (data) => {
    const sendData = {
      ...quizTemplateData,
      ...data,
    }

    const handleNavigateOnSuccess = (response) => {
      if (!response.error) {
        navigate(`/${role}/settings/my-quiz-templates`)
        scrollToTop()
      }
    }

    if (quiz) {
      return dispatch(
        editQuiz({
          id: quiz.quiz_template_hash_1,
          data: sendData,
        })
      ).then(handleNavigateOnSuccess)
    }

    dispatch(addQuiz(sendData)).then(handleNavigateOnSuccess)
  }

  const handleBack = () => {
    setQuizTemplateData({
      ...quizTemplateData,
      ...getValues(),
    })

    setTimeout(() => {
      setStep(2)
    }, 0)
  }

  const handleEditInformation = () => {
    setQuizTemplateData({
      ...quizTemplateData,
      ...getValues(),
    })

    setTimeout(() => {
      setStep(1)
    }, 0)
  }

  useEffect(() => {
    if (quiz && quizTemplateData) {
      if (quizTemplateData.minim_correct_answers) {
        setValue('minim_correct_answers', quizTemplateData.minim_correct_answers)
      }
    }
  }, [quiz, quizTemplateData])

  if (!quizTemplateData) {
    setStep(1)

    return null
  }

  const questionsContent = quizTemplateData.questions.map((question, index) => {
    return <MyQuizTemplateCreatePreviewQuestion key={index} question={question} index={index} />
  })

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="w-full flex flex-col">
      {(error || editError) && (
        <ErrorMessageWithExclamation text={error || editError} className="mb-[20px]" scroll />
      )}

      <div className="w-full flex flex-col items-start gap-y-[20px] p-[32px] rounded-[10px] border-[#F79009] border-[1px]">
        <div className="w-full flex items-center justify-between">
          <h2 className="text-sm font-bold text-primary-100">Quiz Information</h2>

          <button
            type="button"
            className="group/edit ml-auto border-[#F67443] border-[1px] bg-[#FDFDFD] rounded-[6px] h-[40px] pl-[7px] pr-[16px] flex items-center hover:bg-[#F67443] transition-colors duration-200 ease"
            onClick={handleEditInformation}
          >
            <Icon
              name="edit-line"
              className="w-[16px] h-[16px] mr-[6px] text-[#F79009] group-hover/edit:text-white transition-colors duration-200 ease"
            />

            <span className="group-hover/edit:text-white text-primary-100 text-sm font-medium transition-colors duration-200 ease">
              Edit
            </span>
          </button>
        </div>

        <div className="w-full">
          <span className="inline-block text-sm font-medium text-primary-100 mb-[6px]">
            Category
          </span>

          <p className="rounded-[8px] w-full bg-white shadow-button text-base font-normal text-primary-100 flex items-center px-[14px] py-[10px]">
            {category?.name}
          </p>
        </div>

        <div className="w-full">
          <span className="inline-block text-sm font-medium text-primary-100 mb-[6px]">
            Quiz Title
          </span>

          <p className="rounded-[8px] w-full bg-white shadow-button text-base font-normal text-primary-100 flex items-center px-[14px] py-[10px]">
            {quizTemplateData.quiz_title}
          </p>
        </div>

        <div className="w-full">
          <span className="inline-block text-sm font-medium text-primary-100 mb-[6px]">
            Number of question in text
          </span>

          <p className="rounded-[8px] w-full bg-white shadow-button text-base font-normal text-primary-100 flex items-center px-[14px] py-[10px]">
            {quizTemplateData.questions.length}
          </p>
        </div>

        <div className="w-full">
          <label
            htmlFor="minimum-answers"
            className="text-sm font-medium text-primary-100 mb-[6px]"
          >
            Minimum of correct answers to complete
          </label>

          <Input
            type="number"
            id="minimum-answers"
            className="text-primary-100 without-arrows shadow-button"
            placeholder="Type minimum correct answers"
            onWheel={handleNumberInputWheel}
            {...register('minim_correct_answers')}
          />

          {errors.minim_correct_answers ? (
            <ErrorMessageWithExclamation text={errors.minim_correct_answers.message} />
          ) : (
            <p className="mt-[8px] text-xs text-primary-button">
              Please input the number of minimum answers to approve the test
            </p>
          )}
        </div>

        <div className="w-full">
          <span className="inline-block text-sm font-medium text-primary-100 mb-[6px]">
            Due Date to Pass the Quiz
          </span>

          <p className="rounded-[8px] w-full bg-white shadow-button text-base font-normal text-primary-100 flex items-center px-[14px] py-[10px]">
            {moment(quizTemplateData.due_date).format('DD/MM/YYYY')}
          </p>
        </div>
      </div>

      <div className="w-full flex flex-col items-start gap-y-[20px] p-[32px] rounded-[10px] border-[#F79009] border-[1px] mt-[24px]">
        <div className="w-full flex items-center justify-between">
          <h2 className="text-sm font-bold text-primary-100">Quiz Tasks</h2>

          <button
            type="button"
            className="group/edit ml-auto border-[#F67443] border-[1px] bg-[#FDFDFD] rounded-[6px] h-[40px] pl-[7px] pr-[16px] flex items-center hover:bg-[#F67443] transition-colors duration-200 ease"
            onClick={handleBack}
          >
            <Icon
              name="edit-line"
              className="w-[16px] h-[16px] mr-[6px] text-[#F79009] group-hover/edit:text-white transition-colors duration-200 ease"
            />

            <span className="group-hover/edit:text-white text-primary-100 text-sm font-medium transition-colors duration-200 ease">
              Edit
            </span>
          </button>
        </div>

        <div className="w-full flex flex-col gap-y-[20px]">{questionsContent}</div>
      </div>

      <Button
        type="submit"
        isLoading={isLoading || editIsLoading}
        className="w-full font-semibold mt-[24px]"
      >
        Confirm & {quiz ? 'Edit' : 'Create'} Template
      </Button>

      <button
        type="button"
        className="text-base font-semibold text-[#F79009] mx-auto mt-[16px]"
        onClick={handleBack}
      >
        Back
      </button>
    </form>
  )
}

export default MyQuizTemplateCreatePreview
